import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { grey, blue } from "@mui/material/colors";
import axios from "axios";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { PimDashboardState } from "context/PimDashboardContext";
import SelectInputField from "components/PimDashboard/CommonComponents/InputComponents/SelectInputField";
import SearchInputFieldWithDebounce from "components/PimDashboard/CommonComponents/SearchInputFieldWithDebounce/SearchInputFieldWithDebounce";
import { arrType, inputTypes, selectOptionsKeyName, imageColumn } from "data/globalData";
import { ZIPPER_PULLER_CATALOGS } from "components/PimDashboard/DashboardComponents/Sourcing/ZipperPullerMaster/data";
import { THREAD_CATALOGS } from "components/PimDashboard/DashboardComponents/Sourcing/ThreadMaster/data";
import {
	trimBreakdownTableColumns as trimBreakdownColumns,
	breakdownRowUpdatedKey,
	getTrimItemBreakdownRows,
	isTruthyBreakdownLevel,
	trimsTableColumns,
} from "../../data";
import {
	TRIM_SOURCE_TYPES,
	buildZipperBreakdownDescription,
	buildThreadBreakdownDescription,
	getPullerSizeCodeValue,
	trimBreakdownCatalogFields,
	isZipperEditorDraftComplete,
	isThreadEditorDraftComplete,
	pickZipperDraftFromRow,
	pickThreadDraftFromRow,
} from "../../trimCatalogHelpers";
import BreakdownDataGrid from "../../BreakdownDataGrid";
import {
	trimBreakdownAssociationKey,
	buildTrimBreakdownGridColumns,
	buildTrimMasterSearchData,
	getBreakdownCategoryType,
	getRowKey,
	loadBreakdownRowsFromTrim,
	matchesBreakdownRowId,
	patchBreakdownRows,
	syncBreakdownRows,
	trimBreakdownBodySx,
	trimBreakdownCategoryChipSx,
	trimBreakdownCloseButtonSx,
	trimBreakdownEditorSx,
	trimBreakdownEditorTitleSx,
	trimBreakdownBulkSearchInputSx,
	trimBreakdownGridHeight,
	trimBreakdownGridPanelSx,
	trimBreakdownHeaderSx,
	trimBreakdownModalPaperSx,
	trimBreakdownSelectedRowSx,
	trimBreakdownSelectionChipSx,
	trimCodeInlineInputSx,
	renderTrimCodeDisplayCell,
} from "../trimBreakdownShared";

const trimCodeSearchColumn = {
	name: "Trim Code",
	key: trimBreakdownColumns.trim_code.key,
	input_type: inputTypes.SELECT.key,
	[selectOptionsKeyName]: {
		type: arrType,
		list: [],
		getOptionLabel: (item) => ({
			...item,
			key: item?.trim_code ?? item?.id ?? "",
			name: item?.trim_code ? `${item.trim_code}${item?.item_description ? ` - ${item.item_description}` : ""}` : "",
		}),
	},
};

const normalizeTrimMasterOptions = (rows = []) =>
	rows.map((item) => trimCodeSearchColumn[selectOptionsKeyName].getOptionLabel(item));

const TrimBreakdownModal = ({
	open,
	onClose,
	selectedTrim,
	onUpdateTrim,
	isEditMode = false,
	styleColorRows = [],
	styleSizeRows = [],
}) => {
	const { allAttributes } = PimDashboardState();
	const { user } = AuthState();
	const { getDropdownCache, setDropdownDataCache } = GlobalState();
	const selectedTrimRef = useRef(selectedTrim);
	const openedForTrimRef = useRef(null);

	const [breakdownRows, setBreakdownRows] = useState([]);
	const breakdownRowsRef = useRef(breakdownRows);
	breakdownRowsRef.current = breakdownRows;

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [activeRowKey, setActiveRowKey] = useState(null);
	const [bulkEditorDraft, setBulkEditorDraft] = useState({});
	const [bulkEditorResetNonce, setBulkEditorResetNonce] = useState(0);
	const [catalogs, setCatalogs] = useState({ zipperTypes: [], pullers: [], metalFinishes: [], threadItems: [] });

	const categoryType = getBreakdownCategoryType(selectedTrim);
	const trimSizeLevelKey = trimsTableColumns.size_level.key;
	const trimCategoryKey = trimsTableColumns.item_category.key;
	const trimSubCategoryKey = trimsTableColumns.item_sub_category.key;

	useEffect(() => {
		selectedTrimRef.current = selectedTrim;
	}, [selectedTrim]);

	useEffect(() => {
		if (!open) return;
		if (categoryType === "MASTER") return;

		axios
			.get("/api/fabric_trims/get_trim_catalogs")
			.then((res) => {
				const result = res.data?.result ?? {};
				setCatalogs({
					zipperTypes: result.zipper_types ?? [],
					pullers: result.pullers ?? [],
					metalFinishes: result.metal_finishes ?? [],
					threadItems: result.thread_items ?? [],
				});
			})
			.catch(() => {});
	}, [open, categoryType]);

	useEffect(() => {
		if (!open) {
			openedForTrimRef.current = null;
			setSelectedRowKeys([]);
			setActiveRowKey(null);
			setBulkEditorDraft({});
			setBulkEditorResetNonce(0);
			return;
		}

		const trimId = String(selectedTrim?.id ?? "");
		if (openedForTrimRef.current === trimId) return;

		openedForTrimRef.current = trimId;
		const syncedRows = loadBreakdownRowsFromTrim(selectedTrim, {
			colorRows: styleColorRows,
			sizeRows: styleSizeRows,
		});
		setBreakdownRows(syncedRows);

		if (syncedRows.length !== getTrimItemBreakdownRows(selectedTrim).length) {
			onUpdateTrim?.({
				...selectedTrim,
				[trimBreakdownAssociationKey]: syncedRows,
			});
		}

		const firstKey = syncedRows.length ? getRowKey(syncedRows[0]) : null;
		setActiveRowKey(firstKey);
		setSelectedRowKeys(firstKey ? [firstKey] : []);
	}, [open, selectedTrim, selectedTrim?.id, styleColorRows, styleSizeRows, onUpdateTrim]);

	const targetRowKeys = useMemo(() => {
		if (selectedRowKeys.length) return selectedRowKeys;
		return activeRowKey ? [activeRowKey] : [];
	}, [selectedRowKeys, activeRowKey]);

	const isBulkEdit = targetRowKeys.length > 1;

	const bulkEditSelectionKey = useMemo(
		() => [...targetRowKeys].map(String).sort().join("|"),
		[targetRowKeys],
	);

	const composerRow = useMemo(() => {
		if (!targetRowKeys.length) return null;
		return breakdownRows.find((row) => getRowKey(row) === targetRowKeys[0]) ?? null;
	}, [breakdownRows, targetRowKeys]);

	useEffect(() => {
		if (isBulkEdit) {
			setBulkEditorDraft({});
			setBulkEditorResetNonce((nonce) => nonce + 1);
			return;
		}
		if (categoryType === "ZIPPER") {
			setBulkEditorDraft(pickZipperDraftFromRow(composerRow));
		} else if (categoryType === "THREAD") {
			setBulkEditorDraft(pickThreadDraftFromRow(composerRow));
		} else {
			setBulkEditorDraft({});
		}
		setBulkEditorResetNonce((nonce) => nonce + 1);
	}, [bulkEditSelectionKey, isBulkEdit, categoryType, composerRow]);

	const usesDeferredEditorApply =
		isBulkEdit || categoryType === "ZIPPER" || categoryType === "THREAD";

	const editorFormRow =
		categoryType === "ZIPPER" || categoryType === "THREAD" || isBulkEdit
			? bulkEditorDraft
			: composerRow;

	const requiresZipperLength = isTruthyBreakdownLevel(selectedTrim?.[trimSizeLevelKey]);

	const masterTrimPatch = useCallback((selectedMasterTrim = {}) => {
		const trimCode = selectedMasterTrim?.trim_code ?? "";
		let description =
			selectedMasterTrim?.item_description ??
			selectedMasterTrim?.[trimBreakdownColumns.description.key] ??
			"";
		if (!description && selectedMasterTrim?.name && trimCode) {
			const namePrefix = `${trimCode} - `;
			if (String(selectedMasterTrim.name).startsWith(namePrefix)) {
				description = String(selectedMasterTrim.name).slice(namePrefix.length);
			}
		}
		return {
			[trimBreakdownColumns.trim_code.key]: trimCode,
			[trimBreakdownColumns.description.key]: description,
			item_description: description,
			[imageColumn.key]: selectedMasterTrim?.url ?? "",
			[trimBreakdownCatalogFields.trim_source_type]: TRIM_SOURCE_TYPES.TRIM_MASTER,
		};
	}, []);

	const applyPatchToTargets = useCallback(
		(patch) => {
			setBreakdownRows((prev) => {
				if (!targetRowKeys.length) return prev;
				const nextRows = patchBreakdownRows({
					rows: prev,
					targetKeys: targetRowKeys,
					patch,
					userEmail: user?.email,
				});
				onUpdateTrim?.({
					...selectedTrimRef.current,
					[trimBreakdownAssociationKey]: nextRows,
				});
				return nextRows;
			});
		},
		[onUpdateTrim, targetRowKeys, user?.email],
	);

	const applyCatalogPatchToTargets = useCallback(
		(patch, buildDescription) => {
			setBreakdownRows((prev) => {
				if (!targetRowKeys.length) return prev;
				const keySet = new Set(targetRowKeys.map((key) => String(key)));
				const nextRows = syncBreakdownRows(
					prev.map((row) => {
						if (!keySet.has(String(getRowKey(row) ?? ""))) return row;
						const merged = { ...row, ...patch, trim_code: "" };
						return {
							...merged,
							item_description: buildDescription(merged),
							[trimBreakdownColumns.description.key]: buildDescription(merged),
							...(row?.id ? { [breakdownRowUpdatedKey]: true } : {}),
							last_updated_by: user?.email,
						};
					}),
				);
				onUpdateTrim?.({
					...selectedTrimRef.current,
					[trimBreakdownAssociationKey]: nextRows,
				});
				return nextRows;
			});
		},
		[onUpdateTrim, targetRowKeys, user?.email],
	);

	const applyZipperPatch = useCallback(
		(patch) => {
			applyCatalogPatchToTargets(
				{
					...patch,
					puller_code: "",
					puller_size: "",
					[trimBreakdownCatalogFields.trim_source_type]: TRIM_SOURCE_TYPES.ZIPPER,
				},
				(merged) => buildZipperBreakdownDescription(merged, { zipperTypes: catalogs.zipperTypes }),
			);
		},
		[applyCatalogPatchToTargets, catalogs.zipperTypes],
	);

	const applyThreadPatch = useCallback(
		(patch) => {
			applyCatalogPatchToTargets(
				{
					...patch,
					[trimBreakdownCatalogFields.trim_source_type]: TRIM_SOURCE_TYPES.THREAD,
				},
				(merged) => buildThreadBreakdownDescription(merged, { threadItems: catalogs.threadItems }),
			);
		},
		[applyCatalogPatchToTargets, catalogs.threadItems],
	);

	const patchBreakdownRowByGridId = useCallback(
		(gridId, patch) => {
			setBreakdownRows((prev) => {
				const nextRows = prev.map((row) => {
					if (!matchesBreakdownRowId(row, gridId)) return row;
					return {
						...row,
						...patch,
						...(row?.id ? { [breakdownRowUpdatedKey]: true } : {}),
						last_updated_by: user?.email,
					};
				});
				onUpdateTrim?.({
					...selectedTrimRef.current,
					[trimBreakdownAssociationKey]: nextRows,
				});
				return nextRows;
			});
		},
		[onUpdateTrim, user?.email],
	);

	const handleTrimCodeSelectInline = useCallback(
		({ gridId, value }) => {
			patchBreakdownRowByGridId(gridId, masterTrimPatch(value ?? {}));
		},
		[masterTrimPatch, patchBreakdownRowByGridId],
	);

	const handleMasterTrimSelect = useCallback(
		(value) => {
			applyPatchToTargets(masterTrimPatch(value ?? {}));
		},
		[applyPatchToTargets, masterTrimPatch],
	);

	const handleMasterBulkTrimDraft = useCallback((value) => {
		setBulkEditorDraft({ pendingMasterTrim: value ?? null });
	}, []);

	const updateEditorDraft = useCallback((patch) => {
		setBulkEditorDraft((prev) => ({ ...prev, ...patch }));
	}, []);

	const resetEditorDraft = useCallback(() => {
		if (isBulkEdit) {
			setBulkEditorDraft({});
		} else if (categoryType === "ZIPPER") {
			setBulkEditorDraft(pickZipperDraftFromRow(composerRow));
		} else if (categoryType === "THREAD") {
			setBulkEditorDraft(pickThreadDraftFromRow(composerRow));
		} else {
			setBulkEditorDraft({});
		}
		setBulkEditorResetNonce((nonce) => nonce + 1);
	}, [categoryType, composerRow, isBulkEdit]);

	const handleEditorApply = useCallback(() => {
		if (categoryType === "MASTER") {
			if (!bulkEditorDraft.pendingMasterTrim) return;
			handleMasterTrimSelect(bulkEditorDraft.pendingMasterTrim);
			setBulkEditorDraft({});
			setBulkEditorResetNonce((nonce) => nonce + 1);
			return;
		}
		if (categoryType === "ZIPPER") {
			if (!isZipperEditorDraftComplete(bulkEditorDraft)) {
				return;
			}
			applyZipperPatch(bulkEditorDraft);
			if (isBulkEdit) {
				setBulkEditorDraft({});
				setBulkEditorResetNonce((nonce) => nonce + 1);
			}
			return;
		}
		if (categoryType === "THREAD") {
			if (!isThreadEditorDraftComplete(bulkEditorDraft)) return;
			applyThreadPatch(bulkEditorDraft);
			if (isBulkEdit) {
				setBulkEditorDraft({});
				setBulkEditorResetNonce((nonce) => nonce + 1);
			}
		}
	}, [
		applyThreadPatch,
		applyZipperPatch,
		bulkEditorDraft,
		categoryType,
		handleMasterTrimSelect,
		isBulkEdit,
	]);

	const trimMasterFetchCacheKey = useMemo(
		() =>
			[
				selectedTrim?.id ?? "trim",
				selectedTrim?.[trimCategoryKey] ?? "",
				selectedTrim?.[trimSubCategoryKey] ?? "",
			].join("|"),
		[selectedTrim, trimCategoryKey, trimSubCategoryKey],
	);

	const fetchTrimMasterFromSearch = useCallback(
		async (offset = 0, limit = 20, searchTerm = "") => {
			const filter = {
				...(selectedTrim?.[trimCategoryKey] ? { item_category: selectedTrim[trimCategoryKey] } : {}),
				...(selectedTrim?.[trimSubCategoryKey] ? { item_sub_category: selectedTrim[trimSubCategoryKey] } : {}),
				...(searchTerm ? { trim_code: searchTerm } : {}),
			};
			const res = await axios.post("/api/sourcing/trims_master/get", { limit, offset, filter });
			return { ...res, data: { ...res.data, result: res?.data?.result?.rows ?? [] } };
		},
		[selectedTrim, trimCategoryKey, trimSubCategoryKey],
	);

	const trimMasterCacheKey = useMemo(
		() => `trim-master-${trimMasterFetchCacheKey}`,
		[trimMasterFetchCacheKey],
	);

	useEffect(() => {
		if (!open || categoryType !== "MASTER") return;
		if (getDropdownCache(trimMasterCacheKey)?.length) return;

		let cancelled = false;
		(async () => {
			try {
				const res = await fetchTrimMasterFromSearch();
				if (cancelled) return;
				const rows = res?.data?.result ?? [];
				if (rows.length) {
					setDropdownDataCache(trimMasterCacheKey, normalizeTrimMasterOptions(rows));
				}
			} catch {
				// SearchInputField will fetch on its own if pre-warm fails
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		open,
		categoryType,
		trimMasterCacheKey,
		fetchTrimMasterFromSearch,
		getDropdownCache,
		setDropdownDataCache,
	]);

	const fetchTapeColors = useCallback(async (offset = 0, limit = 30, searchTerm = "") => {
		const res = await axios.post("/api/fabric_trims/get_trim_catalog_options", {
			catalog: ZIPPER_PULLER_CATALOGS.TAPE_COLORS,
			limit,
			offset,
			filter: searchTerm ? { tape_color_code: searchTerm } : {},
		});
		const rows = res.data?.result?.rows ?? [];
		return {
			...res,
			data: {
				...res.data,
				result: rows.map((row) => ({ key: row.tape_color_code, name: row.tape_color_code, ...row })),
			},
		};
	}, []);

	const fetchThreadShades = useCallback(async (offset = 0, limit = 30, searchTerm = "") => {
		const res = await axios.post("/api/fabric_trims/get_trim_catalog_options", {
			catalog: THREAD_CATALOGS.THREAD_SHADES,
			limit,
			offset,
			filter: searchTerm ? { shade_code: searchTerm } : {},
		});
		const rows = res.data?.result?.rows ?? [];
		return {
			...res,
			data: {
				...res.data,
				result: rows.map((row) => ({ key: row.shade_code, name: row.shade_code, ...row })),
			},
		};
	}, []);

	const renderTrimCodeCell = useCallback(
		(params) => {
			const { row, id: gridId } = params;

			if (!isEditMode || categoryType !== "MASTER") {
				return renderTrimCodeDisplayCell(params);
			}

			const liveRow = breakdownRowsRef.current.find((r) => matchesBreakdownRowId(r, gridId)) ?? row;

			return (
				<Box
					width="100%"
					height="100%"
					display="flex"
					alignItems="center"
					px={0.75}
					boxSizing="border-box"
					onClick={(event) => event.stopPropagation()}>
					<SearchInputFieldWithDebounce
						column={trimCodeSearchColumn}
						data={buildTrimMasterSearchData(liveRow)}
						setData={() => {}}
						fetchData={fetchTrimMasterFromSearch}
						cacheKey={trimMasterCacheKey}
						selectOptKeyName={selectOptionsKeyName}
						disableLabel
						storeValueAs="object"
						width="100%"
						onChange={(event) =>
							handleTrimCodeSelectInline({ gridId, value: event?.target?.value })
						}
						inputSx={trimCodeInlineInputSx}
					/>
				</Box>
			);
		},
		[
			categoryType,
			fetchTrimMasterFromSearch,
			handleTrimCodeSelectInline,
			isEditMode,
			trimMasterCacheKey,
		],
	);

	const breakdownColumns = useMemo(
		() =>
			buildTrimBreakdownGridColumns({
				allAttributes,
				categoryType,
				renderTrimCodeCell: categoryType === "MASTER" ? renderTrimCodeCell : undefined,
			}),
		[allAttributes, categoryType, isEditMode, renderTrimCodeCell],
	);

	const selectedZipperType = useMemo(
		() => catalogs.zipperTypes.find((item) => item.zipper_type_code === editorFormRow?.zipper_type_code),
		[editorFormRow?.zipper_type_code, catalogs.zipperTypes],
	);

	const filteredPullerSizeOptions = useMemo(() => {
		const zipperSize = selectedZipperType?.size;
		return catalogs.pullers.flatMap((puller) =>
			(puller?.pullerSizes ?? [])
				.filter((item) => !zipperSize || item.size === zipperSize)
				.map((item) => ({
					...item,
					puller_code: puller.puller_code,
					puller_size_code: item.puller_size_code || `${puller.puller_code} ${item.size}`,
				})),
		);
	}, [catalogs.pullers, selectedZipperType?.size]);

	const masterBulkSearchData = useMemo(() => {
		const pending = bulkEditorDraft.pendingMasterTrim;
		if (!pending) return buildTrimMasterSearchData({});
		return { [trimBreakdownColumns.trim_code.key]: pending };
	}, [bulkEditorDraft.pendingMasterTrim]);

	const canApplyEditor = useMemo(() => {
		if (!usesDeferredEditorApply) return false;
		if (categoryType === "MASTER") {
			return Boolean(bulkEditorDraft.pendingMasterTrim?.trim_code);
		}
		if (categoryType === "ZIPPER") {
			return isZipperEditorDraftComplete(bulkEditorDraft);
		}
		if (categoryType === "THREAD") {
			return isThreadEditorDraftComplete(bulkEditorDraft);
		}
		return false;
	}, [bulkEditorDraft, categoryType, usesDeferredEditorApply]);

	const subtitle = [
		selectedTrim?.item_category || "Accessory",
		selectedTrim?.item_sub_category ? ` — ${selectedTrim.item_sub_category}` : "",
	].join("");

	const selectionLabel =
		targetRowKeys.length > 1
			? `Bulk edit · ${targetRowKeys.length} rows`
			: targetRowKeys.length === 1
				? "1 row selected"
				: "Select rows";

	const showEditorPanel =
		isEditMode && composerRow && (categoryType !== "MASTER" || targetRowKeys.length > 1);

	const categoryChipColor =
		categoryType === "ZIPPER"
			? { bgcolor: blue[50], color: blue[800], borderColor: blue[200] }
			: categoryType === "THREAD"
				? { bgcolor: grey[100], color: grey[800], borderColor: grey[300] }
				: { bgcolor: "#f0fdf4", color: "#166534", borderColor: "#bbf7d0" };

	return (
		<Dialog
			onClose={onClose}
			open={open}
			fullWidth
			maxWidth={false}
			scroll="paper"
			PaperProps={{ sx: trimBreakdownModalPaperSx }}>
			<Box sx={trimBreakdownHeaderSx}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
					<Box>
						<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
							<Typography variant="h6" sx={{ fontWeight: 800, color: grey[900], lineHeight: 1.2, letterSpacing: "-0.02em" }}>
								Trim Breakdown
							</Typography>
							<Chip
								size="small"
								label={categoryType}
								sx={{ ...trimBreakdownCategoryChipSx, ...categoryChipColor, border: "1px solid" }}
							/>
							<Chip
								size="small"
								variant="outlined"
								label={`${breakdownRows.length} row${breakdownRows.length === 1 ? "" : "s"}`}
								sx={{ ...trimBreakdownCategoryChipSx, bgcolor: "#fff", color: grey[600], borderColor: grey[300] }}
							/>
						</Stack>
						<Typography variant="body2" sx={{ color: grey[600], maxWidth: 560 }}>
							{subtitle}
						</Typography>
					</Box>
					<Chip size="small" label={selectionLabel} sx={trimBreakdownSelectionChipSx} />
				</Stack>
			</Box>

			<DialogContent sx={{ ...trimBreakdownBodySx, p: 0 }}>
				<Box
					sx={{
						px: { xs: 1.5, sm: 2.5 },
						py: 1.5,
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}>
					{showEditorPanel && (
						<Box sx={trimBreakdownEditorSx}>
							<Typography sx={trimBreakdownEditorTitleSx}>
								{targetRowKeys.length > 1
									? `Apply changes to ${targetRowKeys.length} selected rows`
									: "Configure selected row"}
							</Typography>

							{categoryType === "MASTER" && targetRowKeys.length > 1 && (
								<Box sx={{ maxWidth: 520 }}>
									<SearchInputFieldWithDebounce
										key={`master-bulk-${bulkEditSelectionKey}-${bulkEditorResetNonce}`}
										column={trimCodeSearchColumn}
										data={masterBulkSearchData}
										setData={() => {}}
										fetchData={fetchTrimMasterFromSearch}
										cacheKey={trimMasterCacheKey}
										selectOptKeyName={selectOptionsKeyName}
										storeValueAs="object"
										width="100%"
										disabled={!isEditMode}
										onChange={(event) => handleMasterBulkTrimDraft(event?.target?.value)}
										inputSx={trimBreakdownBulkSearchInputSx}
									/>
								</Box>
							)}

							{categoryType === "ZIPPER" && (
								<Stack
									key={`zipper-editor-${bulkEditSelectionKey}-${bulkEditorResetNonce}`}
									direction="row"
									flexWrap="wrap"
									gap={2}>
									<Box sx={{ minWidth: 240, flex: "1 1 240px" }}>
										<SelectInputField
											required
											column={{ name: "Zipper Type", key: trimBreakdownCatalogFields.zipper_type_code }}
											data={{
												[trimBreakdownCatalogFields.zipper_type_code]:
													editorFormRow?.zipper_type_code ?? null,
											}}
											setData={() => {}}
											options={{
												type: arrType,
												list: catalogs.zipperTypes,
												getOptionLabel: (item) => ({
													key: item.zipper_type_code,
													name: item.code,
												}),
											}}
											width="100%"
											disabled={!isEditMode}
											onChange={(e) =>
												updateEditorDraft({
													zipper_type_code: e.target.value,
													puller_size_code: null,
													metal_finish: "",
													tape_color_code: "",
												})
											}
										/>
									</Box>

									<Box sx={{ minWidth: 240, flex: "1 1 240px" }}>
										<SelectInputField
											required
											column={{
												name: "Puller Size Code",
												key: trimBreakdownCatalogFields.puller_size_code,
											}}
											data={{
												[trimBreakdownCatalogFields.puller_size_code]:
													getPullerSizeCodeValue(editorFormRow) || null,
											}}
											setData={() => {}}
											options={{
												type: arrType,
												list: filteredPullerSizeOptions,
												getOptionLabel: (item) => ({
													key: item.puller_size_code,
													name: item.puller_size_code,
												}),
											}}
											width="100%"
											disabled={!isEditMode || !editorFormRow?.zipper_type_code}
											onChange={(e) => {
												const pullerSize = filteredPullerSizeOptions.find(
													(item) =>
														String(item.puller_size_code) === String(e.target.value),
												);
												updateEditorDraft({
													puller_size_code: pullerSize?.puller_size_code ?? null,
													metal_finish:
														pullerSize?.default_metal_finish ?? editorFormRow?.metal_finish ?? "",
												});
											}}
										/>
									</Box>

									<Box sx={{ minWidth: 200, flex: "1 1 200px" }}>
										<SelectInputField
											required
											column={{ name: "Metal Finish", key: trimBreakdownCatalogFields.metal_finish }}
											data={{
												[trimBreakdownCatalogFields.metal_finish]: editorFormRow?.metal_finish ?? null,
											}}
											setData={() => {}}
											options={{
												type: arrType,
												list: [{ metal_finish_name: "" }, ...catalogs.metalFinishes],
												getOptionLabel: (item) => ({
													key: item.metal_finish_name,
													name: item.metal_finish_name || "None",
												}),
											}}
											width="100%"
											disabled={!isEditMode}
											onChange={(e) =>
												updateEditorDraft({ metal_finish: e.target.value || "" })
											}
										/>
									</Box>

									<Box sx={{ minWidth: 200, flex: "1 1 200px" }}>
										<SearchInputFieldWithDebounce
											column={{
												name: "Tape Color",
												key: trimBreakdownCatalogFields.tape_color_code,
												input_type: inputTypes.SELECT.key,
												[selectOptionsKeyName]: {
													type: arrType,
													list: [],
													getOptionLabel: (item) => ({
														...item,
														key: item.tape_color_code,
														name: item.tape_color_code,
													}),
												},
											}}
											data={{
												[trimBreakdownCatalogFields.tape_color_code]:
													editorFormRow?.tape_color_code || "",
											}}
											setData={() => {}}
											fetchData={fetchTapeColors}
											cacheKey="zipper-tape-colors"
											selectOptKeyName={selectOptionsKeyName}
											width="100%"
											disabled={!isEditMode}
											onChange={(event) =>
												updateEditorDraft({
													tape_color_code:
														event?.target?.value?.tape_color_code ??
														event?.target?.value ??
														"",
												})
											}
										/>
									</Box>

									{requiresZipperLength && (
										<TextField
											label="Zipper Length"
											size="small"
											sx={{ minWidth: 140, flex: "0 0 140px" }}
											value={editorFormRow?.zipper_length || ""}
											disabled={!isEditMode}
											onChange={(e) => updateEditorDraft({ zipper_length: e.target.value })}
										/>
									)}
								</Stack>
							)}

							{categoryType === "THREAD" && (
								<Stack
									key={`thread-editor-${bulkEditSelectionKey}-${bulkEditorResetNonce}`}
									direction="row"
									flexWrap="wrap"
									gap={2}>
									<Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
										<SelectInputField
											required
											column={{
												name: "Thread Item",
												key: trimBreakdownCatalogFields.thread_item_code,
											}}
											data={{
												[trimBreakdownCatalogFields.thread_item_code]:
													editorFormRow?.thread_item_code ?? null,
											}}
											setData={() => {}}
											options={{
												type: arrType,
												list: catalogs.threadItems,
												getOptionLabel: (item) => ({
													key: item.thread_item_code,
													name: item.code,
												}),
											}}
											width="100%"
											disabled={!isEditMode}
											onChange={(e) =>
												updateEditorDraft({
													thread_item_code: e.target.value,
													thread_shade_code: "",
												})
											}
										/>
									</Box>

									<Box sx={{ minWidth: 220, flex: "1 1 220px" }}>
										<SearchInputFieldWithDebounce
											column={{
												name: "Thread Shade",
												key: trimBreakdownCatalogFields.thread_shade_code,
												input_type: inputTypes.SELECT.key,
												[selectOptionsKeyName]: {
													type: arrType,
													list: [],
													getOptionLabel: (item) => ({
														...item,
														key: item.shade_code,
														name: item.shade_code,
													}),
												},
											}}
											data={{
												[trimBreakdownCatalogFields.thread_shade_code]:
													editorFormRow?.thread_shade_code || "",
											}}
											setData={() => {}}
											fetchData={fetchThreadShades}
											cacheKey="thread-shades"
											selectOptKeyName={selectOptionsKeyName}
											width="100%"
											disabled={!isEditMode}
											onChange={(event) =>
												updateEditorDraft({
													thread_shade_code:
														event?.target?.value?.shade_code ??
														event?.target?.value ??
														"",
												})
											}
										/>
									</Box>
								</Stack>
							)}

							{usesDeferredEditorApply && (
								<Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} mt={1.5}>
									{isBulkEdit && (
										<Button variant="outlined" size="small" onClick={resetEditorDraft} sx={trimBreakdownCloseButtonSx}>
											Reset
										</Button>
									)}
									<Button
										variant="contained"
										size="small"
										disabled={!isEditMode || !canApplyEditor}
										onClick={handleEditorApply}>
										Apply
									</Button>
								</Stack>
							)}
						</Box>
					)}

					<Box sx={trimBreakdownGridPanelSx}>
						<BreakdownDataGrid
							key={isEditMode ? "trim-breakdown-edit" : "trim-breakdown-view"}
							columns={breakdownColumns}
							rows={breakdownRows}
							checkboxSelection={isEditMode}
							rowSelectionModel={selectedRowKeys}
							onRowSelectionModelChange={(model) => {
								setSelectedRowKeys(model);
								if (model.length === 1) setActiveRowKey(model[0]);
							}}
							onRowClick={({ row }) => {
								const key = getRowKey(row);
								setActiveRowKey(key);
								if (!isEditMode) return;
								setSelectedRowKeys((prev) => (prev.includes(key) ? prev : [key]));
							}}
							disableRowSelectionOnClick={false}
							sx={{
								height: trimBreakdownGridHeight(breakdownRows.length, { compact: showEditorPanel }),
								maxHeight: "100%",
								"& .MuiDataGrid-row:hover": {
									backgroundColor: `${grey[50]} !important`,
								},
								"& .selected-breakdown-row": trimBreakdownSelectedRowSx,
								"& .MuiDataGrid-columnHeaderTitle": {
									fontWeight: 700,
									fontSize: "0.72rem",
									letterSpacing: "0.06em",
									textTransform: "uppercase",
									color: grey[700],
								},
							}}
							getRowClassName={({ row }) =>
								getRowKey(row) === activeRowKey ? "selected-breakdown-row" : ""
							}
						/>
					</Box>

					{!isEditMode && (
						<Box
							sx={{
								mt: 1.5,
								flexShrink: 0,
								px: 2,
								py: 1.25,
								borderRadius: 2,
								textAlign: "center",
								border: `1px dashed ${grey[300]}`,
								backgroundColor: "#fff",
								color: grey[600],
								fontSize: "0.875rem",
							}}>
							Enable edit mode to configure breakdown rows.
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions
				sx={{
					px: { xs: 2, sm: 3 },
					pb: 2,
					pt: 1.5,
					flexShrink: 0,
					borderTop: `1px solid ${grey[200]}`,
					backgroundColor: "#fff",
				}}>
				<Button variant="outlined" onClick={onClose} sx={trimBreakdownCloseButtonSx}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TrimBreakdownModal;
